import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    iconSize,
    styledInteraction,
    styledLayout,
    styledMedia,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './tokens';

interface CommonActionProps {
    $compact?: boolean;
    $disabled?: boolean;
}

const actionBase = css<CommonActionProps>`
    ${styledReset.borderBox};
    ${styledLayout.fullWidthFix};

    --yat_price-action-gap: ${({ $compact }) => ($compact ? tokens.wrapper.gap.compact : tokens.wrapper.gap.default)}px;
    --yat_price-action-padding: ${({ $compact }) =>
        $compact ? tokens.wrapper.padding.compact : tokens.wrapper.padding.default}px;

    --yat_price-action-divider-offset: ${({ $compact }) =>
        ($compact ? tokens.divider.offset.compact : tokens.divider.offset.default) * -1}px;

    --yat_price-action-height: ${({ $compact }) => ($compact ? tokens.height.compact : tokens.height.default)}px;
    --yat_price-action-radius: ${({ $compact }) => ($compact ? tokens.radius.compact : tokens.radius.default)}px;

    --yat_price-action-symbol-size: ${({ $compact }) => ($compact ? iconSize.smaller : iconSize.medium)}px;

    --yat_price-action-font-size: ${({ $compact }) => ($compact ? tokens.fontSize.compact : tokens.fontSize.default)}px;
    --yat_price-action-bg-color: ${({ $disabled }) => ($disabled ? color.ash : color.forest)};

    display: flex;
    align-items: center;
    gap: var(--yat_price-action-gap);
    padding-left: var(--yat_price-action-padding);
    padding-right: var(--yat_price-action-padding);
    height: var(--yat_price-action-height);
    background-color: var(--yat_price-action-bg-color);
    border-radius: var(--yat_price-action-radius);
    transition: background-color ${duration.normal}ms ${easing.cubic};

    ${({ $compact }) =>
        styledInteraction.focusRing({
            offset: -tokens.focusRing.offset,
            radius: ($compact ? tokens.radius.compact : tokens.radius.default) + tokens.focusRing.offset,
        })};

    ${({ $disabled }) => $disabled && styledInteraction.interactionDisable};

    &:hover {
        --yat_price-action-bg-color: ${color.forestDark};
    }

    &:active {
        --yat_price-action-bg-color: ${color.forest};
    }
`;

const typographyBase = css`
    ${styledTypography.allianceBase};
    font-size: var(--yat_price-action-font-size);
    font-weight: 700;
    text-transform: uppercase;
    color: ${color.white};
`;

export const Action = styled.button.attrs({ type: 'button' })<CommonActionProps>`
    ${styledReset.button};
    ${actionBase};
`;

export const ExternalLink = styled.a.attrs({
    target: '_blank',
    rel: 'noopener noreferrer',
})<CommonActionProps>`
    ${styledReset.link};
    ${actionBase};
`;

export const InternalLink = styled(Link)<CommonActionProps>`
    ${styledReset.link};
    ${actionBase};
`;

export const Label = styled.p`
    ${typographyBase};
    ${styledTypography.truncate};
`;

export const Price = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;

    // Horizontal divider
    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: var(--yat_price-action-divider-offset);
        display: block;
        border-left: 1px solid rgba(0, 0, 0, 0.1);
        border-right: 1px solid rgba(255, 255, 255, 0.1);
    }
`;

export const Symbol = styled.div`
    ${styledMedia.iconWrapper};
    width: var(--yat_price-action-symbol-size);
    height: var(--yat_price-action-symbol-size);
    mix-blend-mode: hard-light;
`;

export const Value = styled.p`
    ${typographyBase};
    font-variant-numeric: tabular-nums;
    // Adding 'padding' instead of a 'gap' on the parent
    // in order to address better visual alignment.
    padding-left: ${tokens.price.gap}px;
`;
